import { FieldErrorList } from "../../types";
import { Button, Divider, Typography } from "@mui/material";
import { copy } from "../../tools";
import InvitationForm from "./InvitationForm";
import { FormattedMessage, MessageDescriptor } from "react-intl";
import { InvitationType, SaveInvitationInput } from "@/api";

const defaultParticipantData: SaveInvitationInput = {
    pinCodeRequired: false,
    callout: false,
    muted: false,
    type: InvitationType.Basic,
};

type ParticipantFormTableProps = {
    formData: SaveInvitationInput[];
    errors: FieldErrorList;
    updateFormData: (data: SaveInvitationInput[]) => void;
    subTitleId: MessageDescriptor;
    autoGeneratedInvitationIds: number[];
    disabled?: boolean;
};

export default (props: ParticipantFormTableProps) => {
    const { disabled, errors, formData, subTitleId, autoGeneratedInvitationIds } = props;

    const errorsByIndex: { [i: number]: FieldErrorList } = {};

    for (const error of errors) {
        const idx = parseInt(error.path[1]);
        if (!(idx in errorsByIndex)) errorsByIndex[idx] = [];
        errorsByIndex[idx].push(error);
    }

    const updateFormData = (processor: (data: SaveInvitationInput[]) => void) => {
        const newData = copy(formData);
        processor(newData);
        props.updateFormData(newData);
    };

    const createDeleteHandler = (idx: number) => () => {
        updateFormData((newData) => newData.splice(idx, 1));
    };

    return (
        <div>
            <Divider style={{ marginTop: "1em", marginBottom: "0.5em" }} />
            <div style={{ display: "flex", alignItems: "center" }}>
                <div style={{ flexGrow: 1 }}>
                    <Typography variant="h6">
                        <FormattedMessage id="invitations.label" defaultMessage="Invitations" />
                    </Typography>
                    <Typography variant="subtitle2" color="textSecondary">
                        <FormattedMessage {...subTitleId} />
                    </Typography>
                </div>
                <div>
                    <Button
                        onClick={() => updateFormData((data) => data.push(defaultParticipantData))}
                        size="small"
                        variant="contained"
                        color="primary"
                    >
                        <FormattedMessage id="invitations.addNew" defaultMessage="Add new invitation" />
                    </Button>
                </div>
            </div>
            <table style={{ marginTop: "1em", width: "100%" }} cellPadding={5}>
                {formData.length ? (
                    <thead>
                        <tr>
                            <th>
                                <FormattedMessage id="name" defaultMessage="Name" />
                            </th>
                            <th>
                                <FormattedMessage id="email" defaultMessage="Email address" />
                            </th>
                            <th>
                                <FormattedMessage id="phoneNumber" defaultMessage="Phone number" />
                            </th>
                            <th>
                                <FormattedMessage id="type" defaultMessage="Type" />
                            </th>
                            <th>
                                <FormattedMessage id="callout" defaultMessage="Callout" />
                            </th>
                            <th>
                                <FormattedMessage id="pinCode.required.bool" defaultMessage="PIN" />
                            </th>
                            <th> </th>
                        </tr>
                    </thead>
                ) : null}
                <tbody>
                    {formData.map((p, i) => (
                        <InvitationForm
                            changeFormData={(data) => updateFormData((newData) => Object.assign(newData[i], data))}
                            key={i}
                            formData={p}
                            errors={errorsByIndex[i] || []}
                            disabled={disabled || autoGeneratedInvitationIds.includes(p.id)}
                            onDeleteIcon={autoGeneratedInvitationIds.includes(p.id) ? null : createDeleteHandler(i)}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
};
